import { Box, Button, Divider, Grid, TextField, Typography, Switch, FormControlLabel, Chip } from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import { agendaQuestionsListStyle, dividerStyle, questionContainerStyle } from "./styles";
import CustomBtnFilled from "../../../atoms/form-molecules/CustomBtnFilled";
import { handleClickSeperator } from "./helper";

interface QuestionItem {
  question: string;
  expert_note_required: boolean;
}

interface AgendaData {
  questions: string[];
  meta?: Array<{ expert_note_required: boolean }>;
}

interface AgendaEditAndViewProps {
  title: string;
  currentMode: 'view' | 'edit' | 'preview' | 'create';
  agendaData: AgendaData | null;
  isUserInternal: boolean; 
  onSubmit?: (updatedQuestions: QuestionItem[], agendaId?: any) => void;
  agendaId?: any;
  isClient?: boolean;
  isAdminAllowed?: boolean;
  isProjectOpen?: boolean;
  onModeChange?: (mode: 'view' | 'edit' | 'preview' | 'create') => void;
  isCreatingNew?: boolean;
  updateAgendaLoading?: boolean;
  createAgendaLoading?: boolean;
  titleError?: string; 
}

function AgendaEditAndView({
  title,
  agendaData,
  onSubmit,
  agendaId,
  isClient = false,
  isAdminAllowed = true,
  isProjectOpen = true,
  onModeChange,
  updateAgendaLoading,
  createAgendaLoading,
  isCreatingNew = false,
  titleError = '',
  currentMode,
  isUserInternal
}: AgendaEditAndViewProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [text, setText] = useState<string>("");
  const [previewQuestions, setPreviewQuestions] = useState<QuestionItem[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(currentMode === 'preview');
  const [hasInitialized, setHasInitialized] = useState(false);
  const [originalQuestions, setOriginalQuestions] = useState<QuestionItem[]>([]);
  const [allRequired, setAllRequired] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const convertAgendaDataToQuestions = (agendaData: AgendaData | null): QuestionItem[] => {
    if (!agendaData || !agendaData.questions || agendaData.questions.length === 0) return [];
    
    return agendaData.questions.map((question, index) => ({
      question,
      expert_note_required: agendaData.meta && agendaData.meta[index] 
        ? agendaData.meta[index].expert_note_required 
        : false
    }));
  };

  const handlePreviewClick = () => {
    const questionsArray = text
      .split("--> ")
      .map((ques) => ques.trim())
      .filter((ques) => ques !== "");

    const mappedQuestions: QuestionItem[] = questionsArray.map((question) => {
      const originalQuestion = originalQuestions.find(
        orig => orig.question.trim().toLowerCase() === question.trim().toLowerCase()
      );
      
      return {
        question,
        expert_note_required: originalQuestion ? originalQuestion.expert_note_required : false
      };
    });

    setPreviewQuestions(mappedQuestions);
    setIsPreviewMode(true);
    if (isCreatingNew) {
      setHasInitialized(true);
    }
  };

  const handleBackToEdit = () => {
    setIsPreviewMode(false);
  };

  const handleSubmit = () => {
    if (previewQuestions.length === 0) {
      return;
    }

    if (onSubmit) {
      if (isCreatingNew) {
        onSubmit(previewQuestions);
      } else {
        onSubmit(previewQuestions, agendaId);
      }
    }
  };

  const handleExpertNoteToggle = (index: number, expert_note_required: boolean) => {
    setPreviewQuestions(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, expert_note_required } : item
      )
    );
  };

  const handleAllRequiredToggle = (checked: boolean) => {
    setAllRequired(checked);
    setPreviewQuestions(prev =>
      prev.map(item => ({ ...item, expert_note_required: checked }))
    );
  };

  useEffect(() => {
  if (currentMode === 'view') {
    setIsEditMode(false);
    setIsPreviewMode(false);
  }
}, [currentMode]);
  useEffect(() => {
    const normalizedQuestions = convertAgendaDataToQuestions(agendaData);
    
    if (isCreatingNew && !hasInitialized) {
      setText("--> ");
      setIsEditMode(true);
      setIsPreviewMode(false);
      setOriginalQuestions([]);
      setHasInitialized(true);
    } else if (!isCreatingNew && normalizedQuestions.length > 0) {
      const textValue = "--> " + normalizedQuestions.map(q => q.question).join("\n--> ");
      setText(textValue);
      setOriginalQuestions(normalizedQuestions); // Store original questions
      setHasInitialized(true);
    } else if (!isCreatingNew && normalizedQuestions.length === 0) {
      setText("");
      setOriginalQuestions([]);
      setHasInitialized(true);
    }
  }, [agendaData, isCreatingNew, hasInitialized]);

  // Reset states when switching between agendas or modes
  useEffect(() => {
    if (!isCreatingNew) {
      setIsEditMode(false);
      setIsPreviewMode(false);
    }
    setHasInitialized(false);
  }, [agendaId, isCreatingNew]);

  // Notify parent of mode changes
  useEffect(() => {
    if (onModeChange) {
      if (isCreatingNew) {
        onModeChange(isPreviewMode ? 'preview' : 'create');
      } else if (isEditMode) {
        onModeChange(isPreviewMode ? 'preview' : 'edit');
      } else {
        onModeChange('view');
      }
    }
  }, [isEditMode, isPreviewMode, onModeChange, isCreatingNew]);

  useEffect(() => {
    if (previewQuestions.length > 0) {
      const allAreRequired = previewQuestions.every(q => q.expert_note_required);
      setAllRequired(allAreRequired);
    } else {
      setAllRequired(false);
    }
  }, [previewQuestions]);

  const handleEditClick = () => {
    setIsEditMode(true);
    setIsPreviewMode(false);
    
    // When entering edit mode, set the current questions as preview questions
    // This ensures expert_note_required settings are available for matching
    const normalizedQuestions = convertAgendaDataToQuestions(agendaData);
    setPreviewQuestions(normalizedQuestions);
  };

  const handleCancelEdit = () => {
    if (isCreatingNew) {
      if (onModeChange) onModeChange('view');
    } else {
      setIsEditMode(false);
      setIsPreviewMode(false);
      const normalizedQuestions = convertAgendaDataToQuestions(agendaData);
      if (normalizedQuestions.length > 0) {
        setText("--> " + normalizedQuestions.map(q => q.question).join("\n--> "));
      }
    }
  };

  if ((!title && !agendaData && !isCreatingNew) || (isCreatingNew && !isEditMode && !isPreviewMode)) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          color: "text.secondary",
          textAlign: "center",
          p: 2
        }}
      >
        <Typography variant="body2">
          {isCreatingNew
            ? "Start creating your new agenda by adding questions"
            : "Click on an agenda from the list to preview its questions"}
        </Typography>
      </Box>
    );
  }

  // Edit Mode - Show textarea (for both edit and create)
  if (isEditMode && !isPreviewMode && isUserInternal) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          <textarea
            ref={textRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="text-field-agenda"
            style={{
              width: '100%',
              height: '100%',
              padding: '12px',
              border: '1px solid #FAFAFA',
              backgroundColor: '#FAFAFA',
              borderRadius: '10px',
              fontSize: '14px',
              resize: 'vertical',
              whiteSpace: 'pre-wrap'
            }}
            placeholder="Enter your questions here..."
          />
        </Box>
        <Divider />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          paddingX={2}
          paddingY={1.3}
        >
          <Button
            onClick={() => handleClickSeperator("\n--> ", textRef, setText)}
            variant="outlined"
            size="small"
          >
            Insert Question Separator
          </Button>

          <Box display="flex" gap={1}>
            <CustomBtnFilled
              label="Cancel"
              variant="outlined"
              onClick={handleCancelEdit}
            />
            <CustomBtnFilled
              label="Preview"
              variant="contained"
              onClick={handlePreviewClick}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  if ((isEditMode || isCreatingNew) && isPreviewMode) {
    return (
      <>
        {/* Header Row */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Questions
          </Typography>
          {isUserInternal && previewQuestions.length > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={allRequired}
                  onChange={(e) => handleAllRequiredToggle(e.target.checked)}
                  size="small"
                  color="primary"
                />
              }
              label={
                <Typography color="text.secondary" variant="body2" >
                  All Expert Notes Required
                </Typography>
              }
              labelPlacement="start"
              sx={{ ml: 0 }}
            />
          )}
        </Box>
        <Grid container sx={agendaQuestionsListStyle}>
          {previewQuestions.length ? (
            <>
              {previewQuestions.map((questionItem: QuestionItem, index: number) => (
                <React.Fragment key={questionItem.question + index}>
                  <Grid item sx={questionContainerStyle} xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
                        <p className="ques">Q{index + 1}.</p>
                        <p
                          className="ques-content"
                          style={{ whiteSpace: "pre-wrap", flex: 1 }}
                        >
                          {questionItem.question}
                        </p>
                      </Box>
                      {isUserInternal && (
                        <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Switch
                            checked={questionItem.expert_note_required}
                            onChange={(e) => handleExpertNoteToggle(index, e.target.checked)}
                            size="small"
                            color="primary"
                          />
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: '0.65rem',
                              textAlign: 'center',
                              lineHeight: 1.2,
                              maxWidth: '80px'
                            }}
                          >
                            Note Required
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  {index !== previewQuestions.length - 1 && (
                    <Box sx={dividerStyle(false)}></Box>
                  )}
                </React.Fragment>
              ))}
            </>
          ) : (
            <p
              style={{
                color: "#252B3B",
                fontSize: "14px",
              }}
            >
              No questions found. Please add some questions.
            </p>
          )}
        </Grid>

        <Divider />

        {isUserInternal &&
          <Box
            display="flex"
            justifyContent="space-between"
            paddingX={2}
            paddingY={1.3}
          >
            <Button
              onClick={handleBackToEdit}
              variant="outlined"
            >
              Back to Edit
            </Button>

            <Box display="flex" gap={1}>
              <CustomBtnFilled
                label="Cancel"
                variant="outlined"
                onClick={handleCancelEdit}
              />
              <CustomBtnFilled
                label={
                  (updateAgendaLoading || createAgendaLoading)
                    ? "...Submitting"
                    : isCreatingNew
                      ? "Create"
                      : "Submit"
                }
                variant="contained"
                onClick={handleSubmit}
                disabled={updateAgendaLoading || createAgendaLoading || !!titleError}
              />
            </Box>
          </Box>
        }
      </>
    );
  }

  // Default View Mode (only for existing agendas)
  if (!isCreatingNew) {
    const normalizedQuestions = convertAgendaDataToQuestions(agendaData);
    
    return (
      <>
        <Grid container sx={agendaQuestionsListStyle}>
          {normalizedQuestions.length ? (
            <>
              {normalizedQuestions.map((questionItem: QuestionItem, index: number) => (
                <React.Fragment key={questionItem.question + index}>
                  <Grid item sx={questionContainerStyle} xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
                        <p className="ques">Q{index + 1}.</p>
                        <p
                          className="ques-content"
                          style={{ whiteSpace: "pre-wrap", flex: 1 }}
                        >
                          {questionItem.question}
                        </p>
                      </Box>
                      {questionItem.expert_note_required && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label="Note Required"
                            color="primary"
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  {index !== normalizedQuestions.length - 1 && (
                    <Box sx={dividerStyle(false)}></Box>
                  )}
                </React.Fragment>
              ))}
            </>
          ) : (
            <p
              style={{
                color: "#252B3B",
                fontSize: "14px",
                margin: 'auto'
              }}
            >
              No questions found
            </p>
          )}
        </Grid>

        {isUserInternal && <>
            <Divider />
            <Box display="flex" justifyContent="flex-end" paddingX={2} paddingY={1.3}>
              {!isClient && isAdminAllowed && isProjectOpen && (
                <CustomBtnFilled
                  label="Edit"
                  variant="contained"
                  onClick={handleEditClick}
                />
              )}
            </Box>
          </>
        }
      </>
    );
  }

  // Fallback for creating new agenda in view mode
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        color: "text.secondary",
        textAlign: "center",
        p: 2
      }}
    >
      <Typography variant="body2">
        Ready to create a new agenda
      </Typography>
    </Box>
  );
}

export default AgendaEditAndView;